'use client';

import { c as _c } from "react/compiler-runtime";
import { LoadingButton } from '@7f-ui/components/button/loading-button';
import type { NexperienceTimeslotFragment } from '@data-access/lib/gql/experience/documents';
import { parseAsString, useQueryState } from 'nuqs';
import { useState } from 'react';
import { useAppExperienceDayPrices } from '../_hooks/use-app-experience-day-prices';
import { useAppExperienceTimeslots } from '../_hooks/use-app-experience-timeslots';
import type { ExperienceFilter } from '../_lib/experience-state-types';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppExperienceOffersDay } from './app-experience-offers-day';
import type { AppExperienceOffersListItemProps } from './app-experience-offers-list-item';
import { AppExperienceRequestDateBox } from './app-experience-request-date-box';
import { AppSkeletonOfferItemGroup } from './app-skeleton-offer-item-group';
type AppExperienceOffersListProps = Pick<AppExperienceOffersListItemProps, 'mode' | 'onSubmit'>;
export function AppExperienceOffersList(t0) {
  const $ = _c(34);
  const {
    mode,
    onSubmit
  } = t0;
  const {
    filter,
    experience
  } = useExperienceFilterContext();
  const t1 = useAppExperienceTimeslots();
  let dates;
  let timeslotLoadingState;
  if ($[0] !== t1) {
    ({
      dates,
      ...timeslotLoadingState
    } = t1);
    $[0] = t1;
    $[1] = dates;
    $[2] = timeslotLoadingState;
  } else {
    dates = $[1];
    timeslotLoadingState = $[2];
  }
  const {
    isLoading,
    isPlaceholderData,
    isPending,
    isFetching
  } = timeslotLoadingState;
  const isSettled = !isLoading && !isFetching;
  const [previousFilter, setPreviousFilter] = useState(null);
  const [selectedOffer, setSelectedOffer] = useQueryState("selectedOffer", parseAsString);
  const {
    dayPrices,
    arePreliminary: dayPricesArePreliminary,
    isError: dayPricesFailed
  } = useAppExperienceDayPrices(experience.id, dates[0]?.date, dates[dates.length - 1]?.date);
  if (isSettled && previousFilter !== filter) {
    setPreviousFilter(filter);
  }
  const isFilterUpdated = previousFilter !== filter;
  const showSkeleton = isFilterUpdated && isPlaceholderData || dayPricesArePreliminary;
  let t2;
  if ($[3] !== setSelectedOffer) {
    t2 = timeslot => {
      setSelectedOffer(timeslot.id);
    };
    $[3] = setSelectedOffer;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleExpand = t2;
  if (showSkeleton || isPending) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <><AppSkeletonOfferItemGroup /><AppSkeletonOfferItemGroup /><AppSkeletonOfferItemGroup /></>;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t3;
  if ($[6] !== dates || $[7] !== dayPrices || $[8] !== dayPricesFailed || $[9] !== experience || $[10] !== handleExpand || $[11] !== mode || $[12] !== onSubmit || $[13] !== selectedOffer) {
    let t4;
    if ($[15] !== dayPrices || $[16] !== dayPricesFailed || $[17] !== experience || $[18] !== handleExpand || $[19] !== mode || $[20] !== onSubmit || $[21] !== selectedOffer) {
      t4 = t5 => {
        const {
          date,
          timeslots
        } = t5;
        return <AppExperienceOffersDay key={date} day={date} timeslots={timeslots} experience={experience} dayPrice={dayPrices?.get(date)} dayPriceFailed={dayPricesFailed} onSubmit={onSubmit} mode={mode} expandedSlotId={selectedOffer} onExpand={handleExpand} />;
      };
      $[15] = dayPrices;
      $[16] = dayPricesFailed;
      $[17] = experience;
      $[18] = handleExpand;
      $[19] = mode;
      $[20] = onSubmit;
      $[21] = selectedOffer;
      $[22] = t4;
    } else {
      t4 = $[22];
    }
    t3 = dates.map(t4);
    $[6] = dates;
    $[7] = dayPrices;
    $[8] = dayPricesFailed;
    $[9] = experience;
    $[10] = handleExpand;
    $[11] = mode;
    $[12] = onSubmit;
    $[13] = selectedOffer;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[23] !== t3) {
    t4 = <ul className="flex flex-col gap-y-8 sm:gap-y-12">{t3}</ul>;
    $[23] = t3;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] !== timeslotLoadingState) {
    t5 = <div className="mt-6 text-center sm:text-left"><FetchMoreButton {...timeslotLoadingState} /></div>;
    $[25] = timeslotLoadingState;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== experience.publicId || $[28] !== experience.userTimeslotRequestsAllowed) {
    t6 = experience.userTimeslotRequestsAllowed && <div className="mt-6"><AppExperienceRequestDateBox publicId={experience.publicId} /></div>;
    $[27] = experience.publicId;
    $[28] = experience.userTimeslotRequestsAllowed;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== t4 || $[31] !== t5 || $[32] !== t6) {
    t7 = <div className="h-full overflow-y-auto">{t4}{t5}{t6}</div>;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  return t7;
}
type FetchMoreButtonProps = {
  isLoading: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  canFetchMore: boolean;
  fetchMore: VoidFunction;
};
function FetchMoreButton(t0) {
  const $ = _c(3);
  const {
    isLoading,
    isFetching,
    isPlaceholderData,
    canFetchMore,
    fetchMore
  } = t0;
  if (!canFetchMore && (!isLoading || !isPlaceholderData)) {
    return null;
  }
  const t1 = isLoading || isFetching;
  let t2;
  if ($[0] !== fetchMore || $[1] !== t1) {
    t2 = <LoadingButton variant="secondary" isLoading={t1} onClick={fetchMore}>Mehr Termine anzeigen</LoadingButton>;
    $[0] = fetchMore;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}